import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import Drawer from "react-modern-drawer";
import { DateRange } from "./components/DateRange";
import { MultiSelectDropdown } from "./components/MultiSelectDropdown";
import { PropertyMultiSelectDropdown } from "./components/PropertyMultiSelectDropdown";
import { AccountStatusVariant } from "../../constants";
import { getOperatingStatesAndProvinces } from "api/landlord/landlord";

export type Filters = {
  selectedEnrollmentStatuses: string[];
  moveInDateFrom: string;
  moveInDateTo: string;
  moveOutDateFrom: string;
  moveOutDateTo: string;
  leaseStartDateFrom: string;
  leaseStartDateTo: string;
  selectedProperties: number[];
  selectedStates: string[];
};

type Props = {
  showFilters: boolean;
  setShowFilters: (value: boolean) => void;
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  showEnrollmentStatusFilter: boolean;
  isSDK?: boolean;
  statusDropdown?: (props: {
    selectedStatuses: string[];
    onStatusChange: (statuses: string[]) => void;
  }) => React.ReactNode;
  onApply: () => void;
  onCancel: () => void;
  onClearAll?: () => void;
  extraContent?: React.ReactNode;
};

type FilterState = {
  selectedEnrollmentStatuses: string[];
  dates: {
    moveInDateFrom: string;
    moveInDateTo: string;
    moveOutDateFrom: string;
    moveOutDateTo: string;
    leaseStartDateFrom: string;
    leaseStartDateTo: string;
  };
  selections: {
    selectedProperties: number[];
    selectedStates: string[];
  };
};

const getEnrollmentStatusOptions = (isSDK?: boolean) => {
  const baseOptions = [
    { value: AccountStatusVariant.PendingIDV, label: "Pending IDV" },
    { value: AccountStatusVariant.PendingOptIn, label: "Pending opt in" },
    { value: AccountStatusVariant.PendingOptOut, label: "Pending opt out" }
  ];

  if (isSDK)
    return [
      ...baseOptions,
      {
        value: AccountStatusVariant.PendingLeaseVerification,
        label: "Pending lease verification"
      },
      {
        value: AccountStatusVariant.PendingLandlordVerification,
        label: "Pending landlord verification"
      },
      {
        value: AccountStatusVariant.PendingApproval,
        label: "Pending approval"
      }
    ];

  return baseOptions;
};

// Helper functions to convert between labels (for display) and values (for API)
const valuesToLabels = (
  values: string[],
  options: { value: string; label: string }[]
): string[] => {
  return values
    .map((value) => {
      const option = options.find((opt) => opt.value === value);
      return option?.label;
    })
    .filter((label): label is string => label !== undefined);
};

const labelsToValues = (
  labels: string[],
  options: { value: string; label: string }[]
): string[] => {
  return labels
    .map((label) => {
      const option = options.find((opt) => opt.label === label);
      return option?.value;
    })
    .filter((value): value is AccountStatusVariant => value !== undefined);
};

const getInitialFilterState = (filters: Filters): FilterState => {
  return {
    selectedEnrollmentStatuses: filters.selectedEnrollmentStatuses,
    dates: {
      moveInDateFrom: filters.moveInDateFrom,
      moveInDateTo: filters.moveInDateTo,
      moveOutDateFrom: filters.moveOutDateFrom,
      moveOutDateTo: filters.moveOutDateTo,
      leaseStartDateFrom: filters.leaseStartDateFrom,
      leaseStartDateTo: filters.leaseStartDateTo
    },
    selections: {
      selectedProperties: filters.selectedProperties,
      selectedStates: filters.selectedStates
    }
  };
};

export const FilterDrawer = ({
  showFilters,
  setShowFilters,
  filters,
  onFiltersChange,
  showEnrollmentStatusFilter,
  isSDK,
  statusDropdown,
  onApply,
  onCancel,
  onClearAll,
  extraContent
}: Props) => {
  const { data: operatingStates } = useQuery({
    queryKey: ["OPERATING_STATES_AND_PROVINCES"],
    queryFn: getOperatingStatesAndProvinces
  });
  const uniqueStates = useMemo(
    () => operatingStates?.operating_states_and_provinces || [],
    [operatingStates]
  );

  const enrollmentStatusOptions = useMemo(
    () => getEnrollmentStatusOptions(isSDK),
    [isSDK]
  );
  const enrollmentStatusLabels = useMemo(
    () => enrollmentStatusOptions.map((opt) => opt.label),
    [enrollmentStatusOptions]
  );
  const [draftFilters, setDraftFilters] = useState<FilterState>(() =>
    getInitialFilterState(filters)
  );

  // Reset draft state when drawer opens or applied values change
  useEffect(() => {
    if (showFilters) setDraftFilters(getInitialFilterState(filters));
  }, [showFilters, filters]);

  const handleClearAll = () => {
    setDraftFilters({
      selectedEnrollmentStatuses: [],
      dates: {
        moveInDateFrom: "",
        moveInDateTo: "",
        moveOutDateFrom: "",
        moveOutDateTo: "",
        leaseStartDateFrom: "",
        leaseStartDateTo: ""
      },
      selections: {
        selectedProperties: [],
        selectedStates: []
      }
    });
    onClearAll?.();
  };

  const handleApply = () => {
    const { selectedEnrollmentStatuses, dates, selections } = draftFilters;

    onFiltersChange({
      selectedEnrollmentStatuses,
      moveInDateFrom: dates.moveInDateFrom,
      moveInDateTo: dates.moveInDateTo,
      moveOutDateFrom: dates.moveOutDateFrom,
      moveOutDateTo: dates.moveOutDateTo,
      leaseStartDateFrom: dates.leaseStartDateFrom,
      leaseStartDateTo: dates.leaseStartDateTo,
      selectedProperties: Array.isArray(selections.selectedProperties)
        ? selections.selectedProperties
        : [],
      selectedStates: Array.isArray(selections.selectedStates)
        ? selections.selectedStates
        : []
    });
    onApply();
    setShowFilters(false);
  };

  const handleCancel = () => {
    // Reset draft state to applied values
    setDraftFilters(getInitialFilterState(filters));
    onCancel();
    setShowFilters(false);
  };

  const handleFilterChange = <K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => {
    setDraftFilters((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDateChange = (
    dateKey: keyof FilterState["dates"],
    value: string
  ) => {
    setDraftFilters((prev) => ({
      ...prev,
      dates: { ...prev.dates, [dateKey]: value }
    }));
  };

  const handleSelectionChange = (
    selectionKey: keyof FilterState["selections"],
    value: number[] | string[]
  ) => {
    setDraftFilters((prev) => ({
      ...prev,
      selections: { ...prev.selections, [selectionKey]: value }
    }));
  };

  return (
    <Drawer
      className="rounded-l-lg"
      direction="right"
      onClose={() => {
        setShowFilters(false);
      }}
      open={showFilters}
      size={420}
    >
      <div className="flex h-screen flex-col">
        <div className="flex items-center justify-between border-b border-alpha-40 px-6 py-4">
          <p className="text-xl font-bold text-content-1000">Filters</p>
          <IconButton
            HugeIcon={Cancel01Icon}
            variant={IconButtonVariant.BASE}
            size={IconButtonSize.LG}
            borderLess
            className="!justify-end stroke-content-1000 !p-0"
            onClick={() => setShowFilters(false)}
          />
        </div>
        <div className="mx-6 border-b border-alpha-40 py-4">
          <Button variant={ButtonVariant.TEXT} onClick={handleClearAll}>
            Clear all
          </Button>
        </div>
        <div className="flex flex-grow flex-col gap-4 overflow-y-auto px-6 py-4">
          <PropertyMultiSelectDropdown
            label="Property"
            selectedOptions={draftFilters.selections.selectedProperties}
            onSelectionChange={(selected) =>
              handleSelectionChange("selectedProperties", selected)
            }
          />
          {uniqueStates.length > 0 && (
            <MultiSelectDropdown
              label="State"
              options={uniqueStates}
              selectedOptions={draftFilters.selections.selectedStates}
              onSelectionChange={(selected) =>
                handleSelectionChange("selectedStates", selected)
              }
            />
          )}
          <div className="flex flex-col gap-4">
            <DateRange
              label="Lease Start Date"
              fromValue={draftFilters.dates.leaseStartDateFrom}
              toValue={draftFilters.dates.leaseStartDateTo}
              onFromChange={(value) =>
                handleDateChange("leaseStartDateFrom", value)
              }
              onToChange={(value) =>
                handleDateChange("leaseStartDateTo", value)
              }
            />
          </div>
          <div className="flex flex-col gap-4">
            <DateRange
              label="Move In Date"
              fromValue={draftFilters.dates.moveInDateFrom}
              toValue={draftFilters.dates.moveInDateTo}
              onFromChange={(value) =>
                handleDateChange("moveInDateFrom", value)
              }
              onToChange={(value) => handleDateChange("moveInDateTo", value)}
            />
          </div>
          <div className="flex flex-col gap-4">
            <DateRange
              label="Move Out Date"
              fromValue={draftFilters.dates.moveOutDateFrom}
              toValue={draftFilters.dates.moveOutDateTo}
              onFromChange={(value) =>
                handleDateChange("moveOutDateFrom", value)
              }
              onToChange={(value) => handleDateChange("moveOutDateTo", value)}
            />
          </div>
          {statusDropdown
            ? statusDropdown({
                selectedStatuses: draftFilters.selectedEnrollmentStatuses,
                onStatusChange: (statuses) =>
                  handleFilterChange("selectedEnrollmentStatuses", statuses)
              })
            : showEnrollmentStatusFilter && (
                <MultiSelectDropdown
                  label="Enrollment Status"
                  options={enrollmentStatusLabels}
                  selectedOptions={valuesToLabels(
                    draftFilters.selectedEnrollmentStatuses,
                    enrollmentStatusOptions
                  )}
                  onSelectionChange={(selectedLabels) => {
                    const selectedValues = labelsToValues(
                      selectedLabels,
                      enrollmentStatusOptions
                    );
                    handleFilterChange(
                      "selectedEnrollmentStatuses",
                      selectedValues
                    );
                  }}
                  showSearch={false}
                />
              )}
          {extraContent}
        </div>
        <div className="border-t border-alpha-40 px-6 py-4 flex gap-3">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={handleCancel}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            onClick={handleApply}
            className="flex-1"
          >
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
